/**
 * usePriceIndex (#2694) — the cheap catalog-shape read the Preisspiegel SHELL
 * renders on (APPS_LAYER convention: only the hook touches twinService).
 *
 * **What problem this solves.** The page used to gate its entire first paint on
 * the own-price hook, which was then ONE catalog-wide read (~6.700 cells on WF).
 * (#2694 stage 2 has since narrowed that hook to the visible Kategorie and
 * renamed it ``useItemPriceSeries`` — this read is what made the narrowing
 * possible, by answering the shell's questions without it.) But the shell does
 * not need prices at all. The Warengruppe lens, the #2420
 * hide-empty-subtree rule and the tile list itself need exactly two facts per
 * catalog item: **does it have data** and **as of when**. Names, units, price
 * bases and the whole Warengruppe→Kategorie tree are a static catalog compiled
 * into the bundle. So the page was blocking a whole-tenant download in order to
 * decide which of a locally-known list to show.
 *
 * ``GET /twin/preisspiegel/index`` answers precisely those two facts in one row
 * per ``(catalog_item_id, side)`` — bounded by the CATALOG (one row per item ×
 * side), not by how much the farm sold.
 *
 * **It carries no price, by design** (#2388): at this grain a mean would span
 * every counterparty at once. Prices stay with ``useItemPriceSeries`` / the
 * per-item routes, and arrive AFTER the shell is already on screen.
 *
 * The two hazards this hook exists to not fall into are both silent:
 *
 *  1. **A degraded read looks like an empty catalog.** The route's failure
 *     posture is HTTP 200 + ``index: []`` + ``degraded``/``truncated`` — never a
 *     500. Read naively that renders "Noch keine Preisdaten" over a backend
 *     hiccup, telling a farmer with a full season of sales that they have none.
 *     {@link UsePriceIndexResult.unavailable} vs
 *     {@link UsePriceIndexResult.isEmptyCatalog} is that discrimination, and a
 *     caller must gate the empty state on it rather than on ``entries.length``.
 *     The page gates on ``unavailable`` (the dangerous half) and renders from
 *     the cascade's ``hasAnyData``; ``isEmptyCatalog`` is the positive statement
 *     of the same fact, currently used by tests. Note the two differ in exactly
 *     one case — the index reports ids the FE catalog mirror does not know, so
 *     the read is not empty but nothing is renderable. That is mirror drift,
 *     already reported by the console warning below, and the empty state is a
 *     better answer for it than a blank page.
 *  2. **A dateless group looks like fresh data.** ``n > 0`` with no ``last_at``
 *     means the group's date is UNKNOWN, not current — see
 *     {@link PreisspiegelIndexDataQuality}.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { twinService, type PreisspiegelIndexResponse } from '../services/twinService';
import { allItems } from '../pages/apps/preisspiegel/catalog';

/**
 * Query keys. ``index()`` is the CATALOG-WIDE read specifically — a future
 * narrowed read (``?item=``/``?side=``) must extend the key with those params
 * rather than reuse this one, or the two would share a cache entry and the
 * narrow answer would be served as if it were the whole catalog.
 */
export const priceIndexKeys = {
  all: ['preisspiegel', 'index'] as const,
  index: () => [...priceIndexKeys.all, 'catalog'] as const,
};

/** One ``(item, side)`` group, in FE shape. */
export interface PriceIndexEntry {
  /** ``null`` for an observation the crosswalk could not attribute to a catalog item. */
  itemId: string | null;
  side: string | null;
  /**
   * The unit this group's facts were PUBLISHED under (#2944), or ``null`` when
   * they state none (or the server predates the key).
   *
   * Part of the group key, so an item mid-flip arrives as two entries. That is
   * the only signal the page gets that its tile cannot be honestly labelled —
   * see {@link unitsFor}.
   */
  unit: string | null;
  /** BOOKED trade observations — invoice lines. Excludes the price book (#2777). */
  n: number;
  firstAt: string | null;
  lastAt: string | null;
  /**
   * Curated price-book ESTIMATES in this group (#2777). Counted apart from
   * ``n`` because the two are different claims: one is what a buyer paid, the
   * other is what the book says a crop is worth. Both make a tile worth showing;
   * only the first makes it a booked price.
   */
  estimateN: number;
  /** The estimate slice's latest day — the pinned book anchor in practice. */
  estimateLastAt: string | null;
}

export interface PriceIndexDataQuality {
  /** The server's group cap fired — whole items may be missing from the index. */
  truncated: boolean;
  /** Part of the population could not be read at all. Also raised by ``datelessGroups`` / ``mixedUnitItems``. */
  degraded: boolean;
  /**
   * ``degraded`` restricted to its READ cause (#2944): the population could not
   * be fully read, as opposed to being read completely and found unusable.
   *
   * Use this — never bare ``degraded`` — for any copy that claims something is
   * MISSING. The other two causes are complete reads.
   */
  readDegraded: boolean;
  /** Rows whose stored payload UDB could not parse. */
  malformed: number;
  /** Index ROWS returned (the route's ``cell_count`` at this grain). */
  rowCount: number;
  /** Groups with observations but no readable date — "has data, no *Stand*". */
  datelessGroups: number;
  /**
   * Σ ``n`` — the invoice lines standing behind the index, BOOKED only (#2777).
   *
   * This is what the incomplete-read banner quotes as "N Beobachtungen erfasst".
   * A curated reference price is not an observation and nothing was read to
   * produce it, so padding the figure with the price book would overstate the
   * read — in a sentence whose entire job is to admit that it fell short.
   */
  observationCount: number;
  /**
   * Catalog items whose live facts span MORE THAN ONE unit (#2944) — tiles that
   * cannot be given one honest label and therefore render without a unit.
   */
  mixedUnitItems: number;
  /**
   * HOF-19 Phase B — the AMI benchmark index read failed or was truncated
   * (codex P1). Its own flags, never folded into ``degraded``: no own-price
   * entry is missing when they fire. But UNREAD they are the silent failure
   * this hook exists to prevent one lane over — a failed benchmark read hides
   * a benchmark-only tile with every flag green, which for the tenant that
   * licensed the panel reads as "this item has no data" when the truth is
   * "we could not ask".
   */
  benchmarkDegraded: boolean;
  benchmarkTruncated: boolean;
  /**
   * HOF-248 — the same two facts for the PUBLIC population, which the server
   * reports separately because the two reads fail separately: a tenant without
   * an AMI licence has no AMI read to degrade, and a tenant with one can lose
   * either. Folding them together would name the wrong source in the failure
   * sentence, which is the one sentence whose whole job is to be specific.
   */

}

export interface UsePriceIndexResult {
  /** Every group the server returned, in the server's order (NULLS FIRST). */
  entries: PriceIndexEntry[];
  /** Does this catalog item have ≥1 observation? The lens cascade's predicate. */
  itemHasData: (itemId: string) => boolean;
  /**
   * Latest observed day per catalog item, folded across its sides — ``null``
   * when we cannot vouch for one (see {@link lastAtFor}).
   */
  lastAtByItem: Record<string, string | null>;
  /**
   * The units each catalog item's LIVE facts were published under (#2944).
   *
   * Normally one. Two means the item is mid-flip and no single label is honest —
   * the tile suppresses its unit rather than picking one. Empty means nothing
   * stated a unit, which the tile also reports as silence rather than falling
   * back to the catalog. See {@link unitsFor}.
   */
  unitsByItem: Record<string, string[]>;
  /**
   * The units each item's CURATED price-book facts were published under (#2944).
   *
   * Kept apart from {@link unitsByItem}, which hides them behind the trades'
   * units. Only the chart's reference level needs this — it is the one surface
   * that renders the book's own number. See {@link estimateUnitsFor}.
   */
  estimateUnitsByItem: Record<string, string[]>;
  isLoading: boolean;
  error: Error | null;
  dataQuality: PriceIndexDataQuality;
  /**
   * The read produced no usable answer: it threw, or it came back empty ONLY
   * because the server could not read the population. **Not** the same as an
   * empty catalog — the honest statement here is "we do not know", and the page
   * must not render "Noch keine Preisdaten" on it.
   */
  unavailable: boolean;
  /** The catalog is genuinely empty: an empty index with every flag clear. */
  isEmptyCatalog: boolean;
}

const EMPTY_QUALITY: PriceIndexDataQuality = {
  truncated: false,
  degraded: false,
  readDegraded: false,
  malformed: 0,
  rowCount: 0,
  datelessGroups: 0,
  observationCount: 0,
  mixedUnitItems: 0,
  benchmarkDegraded: false,
  benchmarkTruncated: false,
};

const EMPTY_ENTRIES: PriceIndexEntry[] = [];

/** Server row → FE entry. Kept total: an absent count is 0, not NaN. */
function toEntry(row: PreisspiegelIndexResponse['index'][number]): PriceIndexEntry {
  return {
    itemId: row.catalog_item_id ?? null,
    side: row.side ?? null,
    // #2944. Absent on a server that predates the key, and the honest reading of
    // silence is "it did not say" — NOT the catalog's unit, which is precisely
    // the substitution this field exists to remove from the render path.
    unit: row.unit ?? null,
    n: Number.isFinite(row.n) ? row.n : 0,
    firstAt: row.first_at ?? null,
    lastAt: row.last_at ?? null,
    // The route always sends both keys; the guard is for a server that predates
    // #2777, where the honest reading of silence is "no estimates" — which is
    // the pre-split behaviour exactly.
    estimateN: Number.isFinite(row.estimate_n) ? row.estimate_n : 0,
    estimateLastAt: row.estimate_last_at ?? null,
  };
}

/**
 * The catalog item ids the index reports ANY price for — the lens cascade's data
 * predicate, as a pure function of the index rows.
 *
 * **The OR is the #2777 fix, not a widening.** Until 2777c ``n`` counted booked
 * trades and curated price-book estimates together, and the seven #2694 Saatgut
 * tiles — every one of them estimate-only — rode on that fold. 2777c made ``n``
 * booked-only so the two populations could stop being served as one thing, which
 * left this predicate quietly deleting those tiles from the page: an item with a
 * reference price and no trades reported ``n: 0`` and vanished. Reading both
 * counts restores them.
 *
 * It deliberately does NOT say which population an item has. That distinction
 * belongs where the PRICE is rendered (``PriceSeries.estimate``, stamped in
 * ``cells.ts``), not here — a tile marked "geschätzt" is marked because the
 * number on it came from the book, and deriving that from a count one layer
 * away would let the two disagree.
 *
 * Exported so the cascade's own tests can be fed the real fold rather than a
 * re-implementation of it: a local copy would keep passing if this one broke.
 */
export function itemIdsWithData(entries: PriceIndexEntry[]): Set<string> {
  const ids = new Set<string>();
  for (const entry of entries) {
    if (entry.itemId && (entry.n > 0 || entry.estimateN > 0)) ids.add(entry.itemId);
  }
  return ids;
}

/**
 * Fold an item's per-side groups into ONE *Stand* date.
 *
 * The latest across sides — EXCEPT that a single dateless group with
 * observations poisons the answer for the whole item. That group could hold the
 * most recent observation of all; we simply cannot see its date. Quoting the
 * other side's date as "Stand" would state a freshness the read does not
 * support, which is the same lie one level up that ``dateless_groups`` exists to
 * prevent one level down. Exported for the tile read (#2694 stage 2).
 *
 * Dates are compared as strings, which is exactly ordered for the ``YYYY-MM-DD``
 * days this route returns (``bucket_key="date"``) and would NOT be for full
 * timestamps — if the grain ever widens, this needs a real comparison.
 *
 * ── The estimate slice is a FALLBACK, never a contributor (#2777) ────────────
 *
 * An estimate's date is a pinned anchor (``CUSTOM_PRICE_DATE`` = 2023-01-01, held
 * against the Deckungsbeitrag deep link #2538), not a day anything happened. So
 * it answers only where there is no booked date to answer with:
 *
 *  - **Booked date present** → it wins outright. Folding the anchor in with
 *    ``max`` would be harmless while the anchor stays in the past and a silent
 *    freshness lie the moment it does not — the tile would be dated by a price
 *    nobody paid.
 *  - **No booked date at all** (an untraded item) → the anchor IS the honest
 *    *Stand*: "this is what the book said, as of then". Without it every #2694
 *    Saatgut tile renders a price with no date beside it.
 *  - **A dateless BOOKED group** still poisons the whole item, estimate or not.
 *    That group could hold the item's most recent observation; substituting a
 *    book anchor for a date we cannot read would state a freshness the read does
 *    not support, which is the very lie this rule exists to prevent.
 */
export function lastAtFor(entries: PriceIndexEntry[]): string | null {
  let booked: string | null = null;
  let estimated: string | null = null;
  for (const entry of entries) {
    if (entry.n > 0 && !entry.lastAt) return null;
    // `n > 0` gates the booked date as well as the poison rule, because since
    // #2777 `n` is what makes a date BOOKED. A row with no booked observations
    // and a `last_at` anyway is not something this route emits — it sets
    // `last_at: null` on an estimate-only group explicitly — but if one ever
    // arrived, taking it here would silently suppress the estimate fallback and
    // date the tile from a group that says it holds nothing.
    if (entry.n > 0 && entry.lastAt && (booked === null || entry.lastAt > booked)) booked = entry.lastAt;
    if (entry.estimateN > 0 && entry.estimateLastAt && (estimated === null || entry.estimateLastAt > estimated)) {
      estimated = entry.estimateLastAt;
    }
  }
  return booked ?? estimated;
}

/**
 * Fold an item's per-side groups into the units its LIVE facts carry (#2944).
 *
 * The tile is labelled from this. One unit labels it; two mean it is mid-flip
 * and cannot be honestly labelled at all, so the view suppresses the unit rather
 * than picking a winner; none means nothing said, which the view also reports as
 * silence rather than reaching for the catalog.
 *
 * ── Booked wins outright, matching #2777 and matching the server ──────────────
 *
 * Where an item has traded facts, those alone are the population the tile
 * reports — the same rule that keeps a curated reference price out of the
 * headline, the sparkline and the YoY %. A book price quoted in another unit is
 * not one of that tile's facts and must not blank its label. Only an item with
 * no booked facts at all is labelled from the book, which is exactly when the
 * book IS its data (the seven #2694 Saatgut tiles).
 *
 * This has to agree with the server's ``mixed_unit_items``, which computes the
 * same fold — otherwise the page's banner would contradict its own tiles.
 *
 * Order is the server's row order, deduplicated. A group with no live facts
 * (``n`` and ``estimateN`` both zero) contributes nothing: a unit stamped on a
 * row about nothing is not evidence about anything the tile renders.
 */
export function unitsFor(entries: PriceIndexEntry[]): string[] {
  // The cut is on whether the item HAS booked facts, never on whether those
  // facts happened to state a unit. Those are different questions, and
  // conflating them borrows the book's unit for a trade: an item with booked
  // rows carrying `unit: null` plus one curated row would fall through and label
  // a traded tile from the price book. Booked wins means booked wins including
  // its silences — where trades exist and say nothing, the honest answer is
  // nothing.
  const hasBookedFacts = entries.some((entry) => entry.n > 0);
  return hasBookedFacts ? bookedUnits(entries) : estimateUnitsFor(entries);
}

function bookedUnits(entries: PriceIndexEntry[]): string[] {
  const units: string[] = [];
  for (const entry of entries) {
    if (entry.unit && entry.n > 0 && !units.includes(entry.unit)) units.push(entry.unit);
  }
  return units;
}

/**
 * The units an item's CURATED price-book facts were published under (#2944).
 *
 * {@link unitsFor} deliberately hides these behind the trades' units where an
 * item has both, so that a reference price in another unit cannot blank a traded
 * tile's headline (#2777: booked always wins). That is right for the tile, whose
 * headline IS a trade — and exactly wrong for the one surface that renders the
 * book's own number, the chart's reference level. Labelling a €/l book price
 * with the €/kg the farm trades in is #2944 reintroduced inside its own fix, so
 * the book's units are kept reachable separately rather than folded away.
 */
export function estimateUnitsFor(entries: PriceIndexEntry[]): string[] {
  const units: string[] = [];
  for (const entry of entries) {
    if (entry.unit && entry.estimateN > 0 && !units.includes(entry.unit)) units.push(entry.unit);
  }
  return units;
}

/**
 * The whole of the index READ's derivation, as a pure function of the response.
 *
 * Extracted for HOF-271. ``GET /twin/preisspiegel/tiles`` returns this route's
 * body verbatim as one half of its payload, and the tiles hook has to turn it
 * into exactly the same {@link UsePriceIndexResult} this hook does — the lens,
 * the #2420 hide-empty rule and every tile's *Stand* are computed from it. A
 * second copy of that derivation would be free to drift, and the drift would be
 * invisible: both copies produce a plausible page.
 *
 * So there is one copy and three callers. Everything here was already pure; what
 * moved is only the memoisation boundary.
 */
export function buildPriceIndexResult(
  data: PreisspiegelIndexResponse | undefined,
  { isLoading, error }: { isLoading: boolean; error: Error | null },
): UsePriceIndexResult {
  const rows = data?.index;
  const entries = rows ? rows.map(toEntry) : EMPTY_ENTRIES;

  // HOF-19 Phase B / AC 22 — an item whose ONLY data is the AMI benchmark
  // must still surface for the tenant that licensed the panel, or the tile the
  // benchmark lives on is hidden by the very rule (#2420) that keeps empty
  // items off the page. ORed into the has-data fold exactly as `estimateN`
  // already is; for every other tenant the array is empty and the fold — like
  // the page — is byte-identical to before.
  const benchmarkItemIds = new Set<string>();
  for (const row of data?.benchmark_index ?? []) {
    if (row.catalog_item_id && row.n > 0) benchmarkItemIds.add(row.catalog_item_id);
  }

  const itemIds = itemIdsWithData(entries);
  for (const id of benchmarkItemIds) itemIds.add(id);

  // One grouping, two folds. They must see the SAME partition: a tile's *Stand*
  // and its unit both describe one item, and computing them over two different
  // groupings is how they would come to disagree about which rows that item has.
  const byItem = new Map<string, PriceIndexEntry[]>();
  for (const entry of entries) {
    if (!entry.itemId) continue;
    const bucket = byItem.get(entry.itemId);
    if (bucket) bucket.push(entry);
    else byItem.set(entry.itemId, [entry]);
  }

  const lastAtByItem: Record<string, string | null> = {};
  // #2944 — what the tile is allowed to print beside its price.
  const unitsByItem: Record<string, string[]> = {};
  // #2944 — and what the chart's reference LEVEL is allowed to print, which is a
  // different question whenever an item has both populations.
  const estimateUnitsByItem: Record<string, string[]> = {};
  for (const [itemId, group] of byItem) {
    lastAtByItem[itemId] = lastAtFor(group);
    unitsByItem[itemId] = unitsFor(group);
    estimateUnitsByItem[itemId] = estimateUnitsFor(group);
  }

  const dq = data?.data_quality;
  const dataQuality: PriceIndexDataQuality = !dq
    ? EMPTY_QUALITY
    : {
        truncated: dq.truncated,
        degraded: dq.degraded,
        // #2944. On a server that predates the key the honest reading is the old
        // one: back then `degraded` had no mixed-unit cause, so falling back to it
        // reproduces exactly the behaviour that server's payload described.
        readDegraded: typeof dq.read_degraded === 'boolean' ? dq.read_degraded : dq.degraded,
        malformed: dq.malformed,
        rowCount: dq.cell_count,
        datelessGroups: dq.dateless_groups,
        observationCount: entries.reduce((total, e) => total + e.n, 0),
        // #2944. Absent on a pre-#2944 server, where no tile can be mixed anyway
        // because none of them carries a published unit at all.
        mixedUnitItems: Number.isFinite(dq.mixed_unit_items) ? dq.mixed_unit_items : 0,
        // Absent reads as "fine": a pre-HOF-19 server ran no benchmark read that
        // could have failed.
        // ONE pair here, unlike `/cells`: this route folds every benchmark leg
        // into one presence row per item, so the honest claim about that row is
        // that the answer is incomplete — whichever leg failed.
        benchmarkDegraded: Boolean(dq.benchmark_degraded),
        benchmarkTruncated: Boolean(dq.benchmark_truncated),
      };

  // While the read is in flight NEITHER claim is made: an empty `entries` there
  // is just "not yet", and asserting either would flash a verdict the server has
  // not given.
  const settledEmpty = !isLoading && entries.length === 0;

  return {
    entries,
    itemHasData: (itemId: string) => itemIds.has(itemId),
    lastAtByItem,
    unitsByItem,
    estimateUnitsByItem,
    isLoading,
    error,
    dataQuality,
    // Every arm is gated on having NO entries, `error` included. React Query
    // keeps the last good `data` when a REFETCH fails, and this query is
    // `staleTime`-bounded, so a single flaky response over a warm cache would
    // otherwise raise "we cannot tell what you have" directly above a complete,
    // correct grid — and suppress the incomplete-data banner while doing it.
    // Holding stale rows is not the same as holding nothing.
    unavailable: !isLoading && entries.length === 0 && (error !== null || dataQuality.truncated || dataQuality.degraded),
    isEmptyCatalog: settledEmpty && error === null && !dataQuality.truncated && !dataQuality.degraded,
  };
}

/**
 * Observability, mirroring the cell path's mirror-vs-server drift warning
 * (``unmatchedCellItemIds``). Both cases below mean observations exist that no
 * tile will ever render: an id our catalog mirror does not know, and a row the
 * backend crosswalk could not attribute at all. Neither is an error — the
 * index is right to relay them — but both are invisible without this.
 *
 * A hook rather than part of {@link buildPriceIndexResult} because it is a side
 * effect, and HOF-271 gave that builder a second caller: a console warning fired
 * from inside a pure projection would be emitted once per render.
 */
export function useIndexDriftWarnings(
  entries: PriceIndexEntry[],
  dq: PreisspiegelIndexResponse['data_quality'] | undefined,
): void {
  useEffect(() => {
    if (!entries.length) return;
    const known = new Set(allItems().map((i) => i.id));
    const unmatched = [...new Set(entries.map((e) => e.itemId).filter((id): id is string => !!id && !known.has(id)))];
    if (unmatched.length > 0) {
      console.warn(
        `[preisspiegel] ${unmatched.length} index catalog_item_id(s) match no catalog item ` +
          `(mirror-vs-server drift): ${unmatched.join(', ')}`,
      );
    }
    const unattributed = entries.filter((e) => !e.itemId).reduce((total, e) => total + e.n, 0);
    if (unattributed > 0) {
      console.warn(
        `[preisspiegel] ${unattributed} price observation(s) carry no catalog_item_id ` +
          `(unattributed by the crosswalk); no tile can show them`,
      );
    }
  }, [entries]);

  useEffect(() => {
    if (!dq) return;
    if (dq.truncated || dq.degraded) {
      console.warn(
        `[preisspiegel] price index read came back incomplete (truncated=${dq.truncated}, ` +
          `degraded=${dq.degraded}, malformed=${dq.malformed}, dateless_groups=${dq.dateless_groups}); ` +
          `the catalog shown may be MISSING items`,
      );
    }
  }, [dq]);
}

/**
 * The standalone index read.
 *
 * Since HOF-271 the Preisspiegel page does NOT use this — it reads the index as
 * one half of ``/tiles``, which is what removed the round trip the page used to
 * make before it could ask for prices. Kept because ``/index`` is still a route
 * with its own contract, and because a caller wanting only the catalog shape
 * (with none of the cells) should not have to pay for a grid.
 */
export function usePriceIndex(): UsePriceIndexResult {
  const query = useQuery({
    queryKey: priceIndexKeys.index(),
    queryFn: () => twinService.getPreisspiegelIndex(),
    staleTime: 30_000,
  });

  const error = (query.error as Error | undefined) ?? null;
  const result = useMemo(
    () => buildPriceIndexResult(query.data, { isLoading: query.isLoading, error }),
    [query.data, query.isLoading, error],
  );
  useIndexDriftWarnings(result.entries, query.data?.data_quality);
  return result;
}
